import React, { useEffect, useState, useCallback } from "react";
import { CheckCircle2, Circle, FileSignature, HelpCircle, Bell, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const TYPE_ICON = {
  filing: FileSignature,
  request_info: HelpCircle,
  verify_info: HelpCircle,
  reminder: Bell,
  default: ClipboardList,
};

export default function Tasks({ onOpenDoc }) {
  const [tasks, setTasks] = useState([]);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/tasks"); setTasks(data); } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (t) => {
    const next = t.status === "done" ? "open" : "done";
    try { await api.post(`/tasks/${t.id}/status`, { status: next }); await load(); if (next === "done") toast.success("Task completed."); }
    catch { toast.error("Could not update task."); }
  };

  const open = tasks.filter((t) => t.status !== "done");
  const done = tasks.filter((t) => t.status === "done");

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display font-black text-2xl mb-1">Tasks Dox created</h2>
        <p className="text-white/55 text-sm">Dox turns every action item into a task automatically.</p>
      </div>

      {tasks.length === 0 ? (
        <div className="text-center py-12 text-white/40 text-sm">No tasks yet. Dox creates these as work comes in.</div>
      ) : (
        <>
          <div className="space-y-3">
            {open.map((t) => <TaskRow key={t.id} t={t} toggle={toggle} onOpenDoc={onOpenDoc} />)}
            {open.length === 0 && <div className="text-white/40 text-sm">All caught up. Nice.</div>}
          </div>
          {done.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wider text-white/40 font-semibold mb-3">Completed</div>
              <div className="space-y-2 opacity-60">
                {done.map((t) => <TaskRow key={t.id} t={t} toggle={toggle} onOpenDoc={onOpenDoc} />)}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function TaskRow({ t, toggle, onOpenDoc }) {
  const Icon = TYPE_ICON[t.type] || TYPE_ICON.default;
  const done = t.status === "done";
  return (
    <div data-testid={`task-${t.id}`} className="flex items-center gap-3 glass-card p-4">
      <button onClick={() => toggle(t)} data-testid={`task-toggle-${t.id}`} className="shrink-0 text-dox-orange hover:scale-110 transition-transform">
        {done ? <CheckCircle2 size={22} /> : <Circle size={22} className="text-white/30" />}
      </button>
      <div className="min-w-0 flex-1">
        <div className={`text-sm font-medium ${done ? "line-through text-white/40" : "text-white"}`}>{t.title}</div>
        <div className="flex items-center gap-2 text-xs text-white/40 mt-0.5">
          <Icon size={12} /> <span className="capitalize">{t.type.replace("_", " ")}</span>
          {t.due && <span>· due {t.due}</span>}
          {t.meta?.fee && !["None", "Unknown"].includes(t.meta.fee) && <span>· fee {t.meta.fee}</span>}
        </div>
      </div>
      {t.doc_id && onOpenDoc && (
        <button onClick={() => onOpenDoc(t.doc_id)} className="text-xs text-white/50 hover:text-white shrink-0">Open</button>
      )}
    </div>
  );
}
